import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Phone, Clock, User, FileText, Brain, Play, Pause } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { useState, useRef } from "react";
import { ScreeningNotes } from "./ScreeningNotes";
import { AddCandidateFromScreening } from "./AddCandidateFromScreening";
import { JobMatchRecommendations } from "./JobMatchRecommendations";
import { EnhancedTranscript } from "./EnhancedTranscript";
import { AIScreeningSummary } from "./AIScreeningSummary";
import { extractCandidateName } from "@/utils/screeningNameExtraction";

// Extract candidate name from transcript (legacy function for phone extraction)
const extractCandidateNameFromTranscript = (transcript?: string) => {
  if (!transcript) return null;
  
  const namePatterns = [
    /(?:my name is|i'm|i am|this is)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i,
    /(?:i'm|i am)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i
  ];
  
  for (const pattern of namePatterns) {
    const match = transcript.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return null;
};

// Extract phone number from transcript
const extractPhoneNumber = (transcript: string) => {
  const phoneRegex = /(\+?1?[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/;
  const match = transcript.match(phoneRegex);
  return match ? match[0] : null;
};

interface ScreeningSession {
  id: string;
  candidate_id: string;
  session_status: string;
  transcript: string;
  screening_results: any;
  duration_seconds: number;
  created_at: string;
  completed_at?: string;
  is_inbound_call: boolean;
  candidate_lookup_query?: string;
  call_recordings?: any;
  ai_summary?: any;
  candidates?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    candidate_number: string;
  };
}

const ScreeningDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const { data: session, isLoading } = useQuery({
    queryKey: ['screening-session', id],
    queryFn: async () => {
      if (!id) throw new Error('No session ID provided');
      
      // First try to fetch as voice_screening_session
      const { data: voiceSession, error: voiceError } = await supabase
        .from('voice_screening_sessions')
        .select(`
          id,
          candidate_id,
          session_status,
          transcript,
          screening_results,
          duration_seconds,
          created_at,
          completed_at,
          is_inbound_call,
          candidate_lookup_query,
          audio_url,
          ai_summary,
          candidates:candidate_id (
            id,
            first_name,
            last_name,
            email,
            phone,
            candidate_number
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (!voiceError && voiceSession) {
        return voiceSession as any;
      }

      // If not found, try to fetch as call_recording
      const { data: callRecording, error: callError } = await supabase
        .from('call_recordings')
        .select(`
          id,
          recording_url,
          transcript,
          phone_number,
          notes,
          created_at,
          candidate_id,
          candidates:candidate_id (
            id,
            first_name,
            last_name,
            email,
            phone,
            candidate_number
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (callError) throw callError;
      
      // Transform call recording to match expected session format
      if (callRecording) {
        return {
          id: callRecording.id,
          candidate_id: callRecording.candidate_id,
          session_status: 'completed',
          transcript: callRecording.transcript,
          screening_results: null,
          duration_seconds: 0, // Will be calculated from audio if available
          created_at: callRecording.created_at,
          completed_at: callRecording.created_at,
          is_inbound_call: true,
          candidate_lookup_query: null,
          audio_url: callRecording.recording_url,
          phone_number: callRecording.phone_number,
          notes: callRecording.notes,
          candidates: callRecording.candidates
        };
      }

      throw new Error('Session not found');
    },
    enabled: !!id
  });

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/screening">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Screening
            </Link>
          </Button>
        </div>
        <div className="text-center py-8">Loading screening details...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/screening">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Screening
            </Link>
          </Button>
        </div>
        <div className="text-center py-8">Screening session not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/screening">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Screening
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              Screening Session
            </h1>
            <p className="text-sm text-muted-foreground">
              {session.candidates?.first_name && session.candidates?.last_name 
                ? `${session.candidates.first_name} ${session.candidates.last_name} (${session.candidates?.candidate_number})`
                : extractCandidateName(session)
              }
            </p>
          </div>
        </div>
        <Badge className={getStatusColor(session.session_status)}>
          {session.session_status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI Screening Summary */}
          {session.transcript && (
            <AIScreeningSummary 
              sessionId={session.id}
              transcript={session.transcript}
            />
          )}
          {/* Audio Player */}
          {session.audio_url && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Call Recording
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePlayPause}
                      className="flex items-center gap-2"
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      {isPlaying ? 'Pause' : 'Play'}
                    </Button>
                    <div className="text-sm text-muted-foreground">
                      Duration: {formatDuration(session.duration_seconds || 0)}
                    </div>
                  </div>
                  
                  {/* Native HTML5 Audio Player as fallback */}
                  <div className="w-full">
                    <audio
                      ref={audioRef}
                      src={session.audio_url}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      controls
                      className="w-full h-10 bg-muted rounded-md"
                      preload="metadata"
                    >
                      <source src={session.audio_url} type="audio/mpeg" />
                      <source src={session.audio_url} type="audio/wav" />
                      <source src={session.audio_url} type="audio/mp3" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      Recording available
                    </div>
                    <span>•</span>
                    <span>Quality: Standard</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Audio Available */}
          {!session.audio_url && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Call Recording
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6 text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Phone className="h-6 w-6" />
                    </div>
                    <p className="text-sm">No audio recording available</p>
                    <p className="text-xs">Recording may not have been enabled for this call</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Screening Notes */}
          <ScreeningNotes 
            callRecordingId={session.id} 
            initialNotes={session.notes} 
          />

          {/* Add as Candidate */}
          <AddCandidateFromScreening 
            session={{
              ...session,
              phone_number: session.phone_number || (session.transcript ? extractPhoneNumber(session.transcript) : null)
            }} 
          />

          {/* Job Match Recommendations */}
          <JobMatchRecommendations session={session} />

          {/* Enhanced Transcript */}
          {session.transcript && (
            <EnhancedTranscript 
              transcript={session.transcript}
              duration={session.duration_seconds}
            />
          )}

          {/* AI Analysis */}
          {session.screening_results && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  AI Analysis & Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {session.screening_results.summary && (
                    <div>
                      <h4 className="font-medium mb-2">Summary</h4>
                      <p className="text-sm text-muted-foreground">
                        {session.screening_results.summary}
                      </p>
                    </div>
                  )}
                  
                  {session.screening_results.strengths && (
                    <div>
                      <h4 className="font-medium mb-2">Strengths</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {session.screening_results.strengths.map((strength: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-500 mt-1">•</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {session.screening_results.concerns && (
                    <div>
                      <h4 className="font-medium mb-2">Areas of Concern</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {session.screening_results.concerns.map((concern: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {session.screening_results.recommendation && (
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-2">Recommendation</h4>
                      <div className="p-3 bg-muted rounded-md">
                        <p className="text-sm">
                          {session.screening_results.recommendation}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Candidate Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Candidate Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {session.candidates ? (
                <>
                  <div>
                    <div className="text-sm font-medium">Name</div>
                    <div className="text-sm text-muted-foreground">
                      {session.candidates.first_name} {session.candidates.last_name}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium">Candidate Number</div>
                    <div className="text-sm text-muted-foreground">
                      {session.candidates.candidate_number}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium">Email</div>
                    <div className="text-sm text-muted-foreground">
                      {session.candidates.email}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium">Phone</div>
                    <div className="text-sm text-muted-foreground">
                      {session.candidates.phone}
                    </div>
                  </div>

                  <Separator />
                  
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <Link to={`/candidates/${session.candidate_id}`}>
                      View Full Profile
                    </Link>
                  </Button>
                </>
              ) : (
                 <div className="space-y-3">
                   <div>
                     <div className="text-sm font-medium">Unknown Caller</div>
                     <div className="text-sm text-muted-foreground">
                       {extractCandidateName(session)}
                     </div>
                   </div>
                   
                   {session.phone_number && (
                     <div>
                       <div className="text-sm font-medium">Phone Number</div>
                       <div className="text-sm text-muted-foreground">
                         {session.phone_number}
                       </div>
                     </div>
                   )}
                 </div>
              )}
            </CardContent>
          </Card>

          {/* Session Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Session Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-sm font-medium">Status</div>
                <Badge className={getStatusColor(session.session_status)}>
                  {session.session_status}
                </Badge>
              </div>
              
              <div>
                <div className="text-sm font-medium">Type</div>
                <div className="text-sm text-muted-foreground">
                  {session.is_inbound_call ? 'Inbound Call' : 'Outbound Call'}
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium">Duration</div>
                <div className="text-sm text-muted-foreground">
                  {formatDuration(session.duration_seconds || 0)}
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium">Started</div>
                <div className="text-sm text-muted-foreground">
                  {format(new Date(session.created_at), 'MMM d, yyyy h:mm a')}
                </div>
              </div>
              
              {session.completed_at && (
                <div>
                  <div className="text-sm font-medium">Completed</div>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(session.completed_at), 'MMM d, yyyy h:mm a')}
                  </div>
                </div>
              )}

              {session.candidate_lookup_query && (
                <div>
                  <div className="text-sm font-medium">Lookup Query</div>
                  <div className="text-sm text-muted-foreground">
                    {session.candidate_lookup_query}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fraud Detection */}
          {session.call_recordings?.[0] && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Security Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                {session.call_recordings[0].fraud_score !== null && (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Fraud Score</span>
                      <span className="text-sm font-medium">
                        {session.call_recordings[0].fraud_score}/100
                      </span>
                    </div>
                    {session.call_recordings[0].fraud_alerts && session.call_recordings[0].fraud_alerts.length > 0 && (
                      <div>
                        <div className="text-sm font-medium mb-1">Alerts</div>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {session.call_recordings[0].fraud_alerts.map((alert: any, index: number) => (
                            <li key={index}>• {alert.message || alert}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreeningDetail;